/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedNode').BalancedNode} BalancedNode */
/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */

import { useState } from 'react'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { Tooltip, Typography, useTheme } from '@mui/material'
import { CustomTreeItem, ExpandIcon, CollapseIcon } from '../../generic/CustomTreeItem'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartSearch from '../../generic/formComponent/Search'
import { matchesCodeOrName } from '../../generic/utilities/objectStructures/balanced/balancedSearch'
import { isExternalJobNode } from '../../generic/utilities/objectStructures/balanced/balancedColumns'
import { COMPACT_SEARCH_SX, SEARCH_MATCH_BACKGROUND, SEARCH_MATCH_OUTLINE, MULTI_SELECT_BACKGROUND, MULTI_SELECT_OUTLINE, HAS_CHANGES_BORDER } from '../../generic/utilities/objectStructures/balanced/balancedStyles'
import { formatBalancedLabel } from '../../generic/utilities/objectStructures/balanced/balancedLabel'

/**
 * Builds a recursive group tree from a flat node list.
 *
 * The list mixes two node kinds (`nodeType`):
 * - `WORKFLOW` nodes only define the tree's (sub)groups: `currentWorkflow` is
 *   the workflow the group represents and `parentWorkflow` is the parent group's
 *   workflow (null for a root group). They form a hierarchy of arbitrary depth.
 * - `JOB` nodes are the real jobs; each one is placed inside the group whose
 *   `currentWorkflow` matches the job's `currentWorkflow`.
 *
 * Within a group, sub-groups are listed before loose jobs; both are sorted
 * ascending by `order`.
 *
 * Story 146 / S66 (RN30.1, D30): an EXTERNAL JOB node is not listed at all. The tree is the only
 * place a job can be picked up from, so leaving it out is what makes it undraggable,
 * unmulti-selectable and invisible to the n/N progress of its group in one stroke.
 *
 * @param {BalancedNode[]} nodes - Raw node list from `balancedData.nodes`
 * @returns {Array<{id: number, name: string, order: number, children: Array, jobs: BalancedNode[]}>} root groups
 */
const buildTree = (nodes) => {
  const sortByOrder = (arr) => [...arr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  // S46 (RN14.2): jobs are sorted alphanumerically by "code" (localeCompare with
  // the numeric option, so e.g. "JOB-2" sorts before "JOB-10"), replacing the
  // previous "order"-based sort. Groups (WORKFLOW nodes) keep sorting by "order".
  const sortByCode = (arr) => [...arr].sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' }))

  // One group entry per WORKFLOW node, indexed by the workflow it represents.
  const groupMap = {}
  for (const node of nodes) {
    if (node.nodeType !== 'WORKFLOW' || !node.currentWorkflow) continue
    groupMap[node.currentWorkflow.id] = {
      id: node.currentWorkflow.id,
      code: node.currentWorkflow.code,
      name: node.currentWorkflow.name,
      parentId: node.parentWorkflow?.id ?? null,
      order: node.order ?? 0,
      children: [],
      jobs: []
    }
  }

  // Place each JOB into the group matching its currentWorkflow.
  for (const node of nodes) {
    if (node.nodeType !== 'JOB' || !node.currentWorkflow) continue
    if (isExternalJobNode(node)) continue
    const group = groupMap[node.currentWorkflow.id]
    if (group) group.jobs.push(node)
  }

  // Nest groups: attach to parent when present, otherwise it is a root group.
  const roots = []
  for (const group of Object.values(groupMap)) {
    if (group.parentId != null && groupMap[group.parentId]) {
      groupMap[group.parentId].children.push(group)
    } else {
      roots.push(group)
    }
  }

  const finalize = (group) => {
    group.children = sortByOrder(group.children).map(finalize)
    group.jobs = sortByCode(group.jobs)
    return group
  }

  return sortByOrder(roots).map(finalize)
}

/**
 * BOM-style two-state progress of a parent's subtree: `complete` when every job
 * is assigned, `pending` otherwise (HU-05, S17).
 *
 * @param {number} assigned - Assigned jobs in the subtree (`n`)
 * @param {number} total - Total jobs in the subtree (`N`)
 * @returns {'complete'|'pending'}
 */
export const progressState = (assigned, total) => (total > 0 && assigned >= total ? 'complete' : 'pending')

/**
 * Left-panel tree showing all workflow job nodes grouped by workflow.
 *
 * - The group hierarchy is defined by `WORKFLOW` nodes and can nest to any depth.
 * - Each `JOB` node is rendered as a leaf inside the group whose `currentWorkflow`
 *   matches the job's `currentWorkflow`.
 * - Within a group, sub-groups are listed before loose jobs; both sorted by `order`.
 * - Every leaf node is draggable; the full node object is serialised into
 *   `dataTransfer` as `application/json` on drag start.
 *
 * @param {Object}        props
 * @param {BalancedData}  props.balancedData             - Shared balanced state from `Balanced`
 * @param {Function}      [props.onNodeDragStart]        - Called with the dragged {@link BalancedNode} when drag begins
 * @param {Function}      [props.onNodeDragEnd]          - Called when a drag operation ends
 * @param {Set}           [props.disabledNodeIds]        - Set of node IDs that have been assigned and should not be draggable
 * @param {boolean}       [props.disabled]               - When true all nodes are non-draggable (e.g. editing=false)
 * @param {Function}      [props.onNodeDroppedOnTree]    - Called with the node when a column card is dropped back onto the tree
 * @param {Function}      [props.onGroupDroppedOnTree]   - Called with the group nodes when a group is dropped back onto the tree
 * @param {Function}      [props.onNodeSelected]         - Called with the selected {@link BalancedNode} (or null to clear)
 * @param {number|null}   [props.selectedNodeId]         - ID of the currently selected node, controlled by the parent
 * @param {Set<string>}   [props.multiSelection]         - Ids of JOB nodes picked for a group drag (Ctrl/Cmd+click)
 * @param {Function}      [props.onToggleMultiSelect]    - Called with a JOB node to toggle it in the multi-selection
 */
const BalancedFormTree = ({ balancedData, onNodeDragStart, onNodeDragEnd, disabledNodeIds = new Set(), disabled = false, onNodeDroppedOnTree, onGroupDroppedOnTree, onNodeSelected, selectedNodeId = null, multiSelection = new Set(), onToggleMultiSelect = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [search, setSearch] = useState('')

  const nodes = balancedData?.nodes ?? []
  // Tree search highlights matching nodes in place (same behavior as the columns),
  // without hiding the rest.
  const tree = buildTree(nodes)

  /**
   * Notifies the parent when a JOB leaf is selected so the right panel can show
   * its info. Group items (prefixed `group-`) are ignored.
   *
   * @param {React.SyntheticEvent} _event
   * @param {string} itemId - The selected tree item id
   * @returns {void}
   */
  const handleItemSelected = (_event, itemId) => {
    if (!itemId || itemId.startsWith('group-')) return
    const node = nodes.find(n => String(n.id) === String(itemId))
    if (node?.nodeType === 'JOB') onNodeSelected?.(node)
  }

  /**
   * Clears the selection when the click lands on empty panel space rather than on
   * a tree item (any element with `role="treeitem"`, i.e. a node or a group).
   *
   * @param {React.MouseEvent} e
   * @returns {void}
   */
  const handleContainerClick = (e) => {
    if (!e.target.closest('[role="treeitem"]')) onNodeSelected?.(null)
  }

  /**
   * Recursively collects the tree item IDs of a group and all its descendants
   * so the tree can be initialised in a fully expanded state.
   *
   * @param {{ id: number, children: Object[] }} group
   * @returns {string[]} Tree item IDs prefixed with `'group-'`
   */
  const collectExpandedItems = (group) => ['group-' + group.id, ...group.children.flatMap(collectExpandedItems)]
  const allExpandedItems = tree.flatMap(collectExpandedItems)

  /**
   * Serialises the node into the drag payload and notifies the parent so columns
   * can compute drop feedback.
   *
   * @param {DragEvent} event
   * @param {BalancedNode} node - The node being dragged from the tree
   * @returns {void}
   */
  const handleDragStart = (event, node) => {
    // If the dragged node is part of a Ctrl/Cmd+click multi-selection, drag the
    // whole group (S11); otherwise drag the single node (S14).
    if (multiSelection.has(node.id)) {
      const groupNodes = nodes.filter(n => n.nodeType === 'JOB' && multiSelection.has(n.id))
      const payload = { _isGroup: true, _nodes: groupNodes }
      event.dataTransfer.setData('application/json', JSON.stringify(payload))
      event.dataTransfer.effectAllowed = 'move'
      onNodeDragStart?.(payload)
      return
    }
    event.dataTransfer.setData('application/json', JSON.stringify(node))
    event.dataTransfer.effectAllowed = 'move'
    onNodeDragStart?.(node)
  }

  /**
   * Serialises all unassigned job nodes in a group as a single group drag payload.
   *
   * Only nodes absent from `disabledNodeIds` (i.e. not yet assigned to a column)
   * are included. The drag is silently aborted when the group has no unassigned
   * nodes. The payload carries `_isGroup: true` so drop handlers can distinguish
   * it from a single-node drag.
   *
   * @param {DragEvent} event
   * @param {BalancedNode[]} groupNodes - All job nodes collected from the group (and sub-groups)
   */
  const handleGroupDragStart = (event, groupNodes) => {
    const unassigned = groupNodes.filter(n => !disabledNodeIds.has(n.id))
    if (!unassigned.length) return
    const payload = { _isGroup: true, _nodes: unassigned }
    event.dataTransfer.setData('application/json', JSON.stringify(payload))
    event.dataTransfer.effectAllowed = 'move'
    onNodeDragStart?.(payload)
  }

  /**
   * Recursively collects every JOB node contained in a group, including those in
   * nested sub-groups, so a group label can be dragged as a whole.
   *
   * @param {{ jobs: BalancedNode[], children: Object[] }} group
   * @returns {BalancedNode[]}
   */
  const collectJobNodes = (group) => [...group.jobs, ...group.children.flatMap(collectJobNodes)]

  /**
   * Renders a draggable leaf tree item for a single job node.
   *
   * @param {BalancedNode} node - A workflow job node
   */
  const renderNode = (node) => {
    const nodeDisabled = disabled || disabledNodeIds.has(node.id)
    const highlighted = matchesCodeOrName(node.code, node.name, search)
    const multiSelected = multiSelection.has(node.id)
    const hasChanges = node.hasChanges === true
    return (
      <CustomTreeItem
        key={node.id}
        itemId={node.id}
        label={
          <div
            data-search-match={highlighted || undefined}
            data-multi-selected={multiSelected || undefined}
            data-has-changes={hasChanges || undefined}
            draggable={!nodeDisabled}
            onDragStart={nodeDisabled ? undefined : (e) => handleDragStart(e, node)}
            onDragEnd={nodeDisabled ? undefined : () => onNodeDragEnd?.()}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey) {
                // Always swallow the Ctrl/Cmd+click so the tree does not run its
                // info-selection (which would clear the current multi-selection).
                e.stopPropagation()
                // An assigned node is not draggable, so it is not multi-selectable either.
                if (!nodeDisabled) onToggleMultiSelect(node)
              }
            }}
            style={{ cursor: nodeDisabled ? 'default' : 'grab', opacity: nodeDisabled ? 0.3 : 1, display: 'flex', alignItems: 'baseline', gap: '0.5rem', paddingTop: '3px', paddingBottom: '3px', paddingRight: (highlighted || multiSelected) ? '4px' : 0, paddingLeft: hasChanges ? '5px' : (highlighted || multiSelected) ? '4px' : 0, overflow: 'hidden', background: multiSelected ? MULTI_SELECT_BACKGROUND : highlighted ? SEARCH_MATCH_BACKGROUND : 'transparent', outline: multiSelected ? MULTI_SELECT_OUTLINE : highlighted ? SEARCH_MATCH_OUTLINE : 'none', borderLeft: hasChanges ? HAS_CHANGES_BORDER : 'none', borderRadius: (highlighted || multiSelected) ? '3px' : 0 }}
          >
            <Typography variant='caption' color='primary' style={{ fontWeight: 600, flexShrink: 0 }}>
              {String(node.order ?? '').padStart(3, '0')}
            </Typography>
            <Tooltip title={formatBalancedLabel(node.code, node.name)} placement='right' enterDelay={500}>
              <Typography variant='body2' noWrap style={{ flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {formatBalancedLabel(node.code, node.name)}
              </Typography>
            </Tooltip>
            {node.jobReference?.duration && (
              <Typography variant='caption' style={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)' }}>
                {node.jobReference.duration}
              </Typography>
            )}
          </div>
        }
      />
    )
  }

  /**
   * Recursively renders a workflow group: its nested sub-groups first, then its
   * loose job leaves. Root groups (`depth === 0`) use the uppercase top style;
   * nested groups are rendered in italic.
   *
   * @param {{ id: number, name: string, children: Object[], jobs: BalancedNode[] }} group
   * @param {number} depth - Nesting depth, 0 for root groups
   */
  const renderGroup = (group, depth) => {
    const highlighted = matchesCodeOrName(group.code, group.name, search)
    const subtreeJobs = collectJobNodes(group)
    const total = subtreeJobs.length
    const assigned = subtreeJobs.filter(j => disabledNodeIds.has(j.id)).length
    const state = progressState(assigned, total)
    const progressColor = state === 'complete' ? theme.palette.success.main : theme.palette.warning.main
    return (
      <CustomTreeItem
        key={'group-' + group.id}
        itemId={'group-' + group.id}
        label={
          <div
            data-search-match={highlighted || undefined}
            data-group-progress={total > 0 ? assigned + '/' + total : undefined}
            data-group-progress-state={total > 0 ? state : undefined}
            draggable={!disabled}
            onDragStart={disabled ? undefined : (e) => handleGroupDragStart(e, collectJobNodes(group))}
            onDragEnd={disabled ? undefined : () => onNodeDragEnd?.()}
            style={{ cursor: disabled ? 'default' : 'grab', display: 'inline-flex', alignItems: 'baseline', gap: '0.4rem', padding: highlighted ? '1px 4px' : 0, background: highlighted ? SEARCH_MATCH_BACKGROUND : 'transparent', outline: highlighted ? SEARCH_MATCH_OUTLINE : 'none', borderRadius: highlighted ? '3px' : 0 }}
          >
            {depth === 0
              ? (
                <Typography variant='caption' color='primary' style={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {formatBalancedLabel(group.code, group.name)}
                </Typography>
                )
              : (
                <Typography variant='body2' color='primary' style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                  {formatBalancedLabel(group.code, group.name)}
                </Typography>
                )}
            {total > 0 && (
              <Typography variant='caption' style={{ flexShrink: 0, fontWeight: 600, color: progressColor }}>
                {assigned}/{total}
              </Typography>
            )}
          </div>
        }
      >
        {group.children.map(child => renderGroup(child, depth + 1))}
        {group.jobs.map(renderNode)}
      </CustomTreeItem>
    )
  }

  /**
   * Accepts a node dropped back onto the tree. Only nodes coming from a column
   * (carrying `_sourceColumnKey`) trigger an unassign; tree-to-tree drops are ignored.
   *
   * @param {DragEvent} e
   * @returns {void}
   */
  const handleDropOnTree = (e) => {
    e.preventDefault()
    try {
      const node = JSON.parse(e.dataTransfer.getData('application/json'))
      if (node._isGroup) {
        onGroupDroppedOnTree?.(node._nodes)
      } else if (node._sourceColumnKey) {
        onNodeDroppedOnTree?.(node)
      }
    } catch {
      // ignore malformed drag data
    }
  }

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDropOnTree}
      onClick={handleContainerClick}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0.5rem 0.25rem' }}
    >
      <Typography variant='caption' color='primary' style={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', flexShrink: 0 }}>
        {t_('Workflow jobs')}
      </Typography>
      {nodes.length > 0 && (
        <div
          style={{ flexShrink: 0, marginBottom: '0.5rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          <SmartSearch
            setFilterText={setSearch}
            loadTable={false}
            setLoadTable={() => {}}
            sx={{ width: '100%', ...COMPACT_SEARCH_SX }}
          />
        </div>
      )}
      {nodes.length === 0
        ? (
          <Typography variant='body2' color='text.secondary'>{t_('No jobs')}</Typography>
          )
        : (
          <div style={{ overflowY: 'auto', flexGrow: 1 }}>
            <SimpleTreeView
              aria-label='workflow-jobs-tree'
              selectedItems={selectedNodeId ?? null}
              onSelectedItemsChange={handleItemSelected}
              defaultExpandedItems={allExpandedItems}
              slots={{
                expandIcon: ExpandIcon,
                collapseIcon: CollapseIcon
              }}
              sx={{ overflowX: 'hidden' }}
            >
              {tree.map(group => renderGroup(group, 0))}
            </SimpleTreeView>
          </div>
          )}
    </div>
  )
}

export default BalancedFormTree
